import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Loader from "./Loader";
import normalizeAxiosDetail from "../utils/normalizeAxiosDetail";
import {
  getUTCTimeFromRange,
  MONITOR_HISTORY_RANGE_OPTIONS,
} from "../utils/monitorChartUtils";

/** Default interval tokens (uptime upstream); omit from UI when `showInterval` is false */
export const MONITOR_DEFAULT_INTERVAL_OPTIONS = ["5m", "30m", "1h", "1d"];

/** Shared latency + traffic MB series (monitor uptime/VPN payloads). */
/** One label for uptime + VPN latency (same metric: RTT / latency to target). */
export const MONITOR_LATENCY_LABEL = "Latency (ms)";

export const MONITOR_LATENCY_TRAFFIC_SERIES = {
  responseTime: {
    toolTipCalculate: (value) => (value ? `${value} ms` : "null"),
    label: MONITOR_LATENCY_LABEL,
    color: "#5bdb89",
    axis: "left",
  },
  receivedData: {
    toolTipCalculate: (value) => (value ? `${value} MB` : "null"),
    label: "Received Data",
    color: "#42A5F5",
    axis: "right",
  },
  transmitData: {
    toolTipCalculate: (value) => (value ? `${value} MB` : "null"),
    label: "Transmit Data",
    color: "#FFA726",
    axis: "right",
  },
};

const DEFAULT_DEBOUNCE_MS = 500;

/** Taller plot: more room for Y-axis ticks and headroom above series max (avoids clipped peaks). */
const CHART_HEIGHT_PX = 460;

function collectLatencyMsPoints(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => r?.responseTime)
    .filter((v) => typeof v === "number" && !Number.isNaN(v));
}

function collectTrafficMbPoints(rows) {
  if (!Array.isArray(rows)) return [];
  const keys = ["receivedData", "transmitData"];
  const out = [];
  for (const r of rows) {
    for (const k of keys) {
      const v = r[k];
      if (v == null) continue;
      const n = typeof v === "string" ? parseFloat(v, 10) : Number(v);
      if (!Number.isNaN(n) && n >= 0) out.push(n);
    }
  }
  return out;
}

/**
 * Y domain with headroom above max; lower bound lifts above zero when samples sit away from zero
 * (uses vertical scale better — "clips" unused band below). If values hug zero, ymin stays 0.
 */
function computeSmartYDomain(
  values,
  {
    hugZeroAbsolute = 0,
    hugZeroRelative = 0.1,
    bottomPadFrac = 0.1,
    topPadFrac = 0.14,
    fallbackMin = 0,
    fallbackMax = 1,
    roundMs = false,
    roundMb = false,
  } = {}
) {
  const finite = values.filter((v) => typeof v === "number" && !Number.isNaN(v) && v >= 0);
  if (!finite.length) return [fallbackMin, fallbackMax];

  let dMin = Math.min(...finite);
  let dMax = Math.max(...finite);
  let span = dMax - dMin;
  const eps = 1e-12;

  if (span <= eps) {
    const pad = Math.max(dMax * 0.06, hugZeroAbsolute * 0.5, 0.001);
    const ymin = dMin <= hugZeroAbsolute ? 0 : Math.max(0, dMin - pad * 2);
    const ymax = dMax + pad * 4;
    return normalizeDomainPair(ymin, ymax, { roundMs, roundMb });
  }

  const hugsZero =
    dMin <= hugZeroAbsolute || (dMax > eps && dMin / dMax <= hugZeroRelative);

  let ymin = hugsZero ? 0 : Math.max(0, dMin - bottomPadFrac * span);
  let ymax = dMax + topPadFrac * Math.max(span, dMax * 0.05);

  if (!hugsZero && ymin > 0 && ymin >= dMin) {
    ymin = Math.max(0, dMin - bottomPadFrac * span * 1.05);
  }

  return normalizeDomainPair(ymin, ymax, { roundMs, roundMb });
}

function normalizeDomainPair(ymin, ymax, { roundMs, roundMb }) {
  if (ymax <= ymin) ymax = ymin + (roundMs ? 1 : roundMb ? 0.01 : 1);
  if (roundMs) return [Math.floor(ymin), Math.ceil(ymax)];
  if (roundMb)
    return [Math.floor(ymin * 100) / 100, Math.ceil(ymax * 100) / 100];
  return [ymin, ymax];
}

/** Recharts may omit null series from payload; still show latency row from full datum. */
function MonitorTooltipBody({ active, payload, label, seriesDefinition: def, rows }) {
  if (!active) return null;

  let row = payload?.[0]?.payload;
  if ((!row || !row.timestamp) && label != null && Array.isArray(rows)) {
    const labelStr = label instanceof Date ? label.toISOString() : String(label);
    row =
      rows.find((d) => d?.timestamp === labelStr) ||
      rows.find((d) => new Date(d.timestamp).getTime() === new Date(label).getTime()) ||
      row;
  }

  if (!row?.timestamp) return null;
  const tsLabel = new Date(row.timestamp).toLocaleString();
  return (
    <Box
      sx={{
        bgcolor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid rgba(204, 204, 204, 0.9)",
        borderRadius: 1,
        p: 1,
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" display="block" gutterBottom>
        Timestamp: {tsLabel}
      </Typography>
      {Object.entries(def).map(([dataKey, cfg]) => (
        <Typography key={dataKey} variant="body2" style={{ color: cfg.color }}>
          {`${cfg.label}: ${cfg.toolTipCalculate(row[dataKey])}`}
        </Typography>
      ))}
    </Box>
  );
}

function renderAlertBody(error) {
  if (typeof error !== "string") return error;
  const lines = error.split("\n");
  return lines.map((line, i) => (
    <p key={`${i}-${line}`}>{line}</p>
  ));
}

/**
 * Recharts latency + RX/TX monitor graph.
 *
 * Optional **History** and **Interval** controls; timestamp is always `getUTCTimeFromRange`
 * using visible history (`showHistory`) or `fallbackTimeRange` when history is hidden.
 *
 * `loadChartState`: `interval` is `null` when `showInterval` is false.
 */
export default function MonitorLineChart({
  deviceUuid,
  loadChartState,
  showHistory = true,
  showInterval = false,
  historyOptions = MONITOR_HISTORY_RANGE_OPTIONS,
  /** Initial / current history key when History control is visible */
  defaultHistory = "3h",
  /** ISO window used when History control is hidden (no UI) */
  fallbackTimeRange = "1h",
  intervalOptions = MONITOR_DEFAULT_INTERVAL_OPTIONS,
  defaultInterval = "5m",
  seriesDefinition = MONITOR_LATENCY_TRAFFIC_SERIES,
  /** First paint treats as fetching (VPN tab mounts). */
  initialLoading = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}) {
  const [timeRange, setTimeRange] = useState(defaultHistory);
  const [interval, setInterval] = useState(defaultInterval);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(initialLoading);
  const [chartState, setChartState] = useState(undefined);

  const requestGen = useRef(0);

  const effectiveRangeKey = showHistory ? timeRange : fallbackTimeRange;

  const leftYDomain = useMemo(
    () =>
      computeSmartYDomain(collectLatencyMsPoints(chartState?.data), {
        hugZeroAbsolute: 15,
        hugZeroRelative: 0.085,
        bottomPadFrac: 0.1,
        topPadFrac: 0.13,
        fallbackMin: 0,
        fallbackMax: 520,
        roundMs: true,
      }),
    [chartState?.data]
  );

  const rightYDomain = useMemo(
    () =>
      computeSmartYDomain(collectTrafficMbPoints(chartState?.data), {
        hugZeroAbsolute: 0.04,
        hugZeroRelative: 0.1,
        bottomPadFrac: 0.09,
        topPadFrac: 0.16,
        fallbackMin: 0,
        fallbackMax: 128,
        roundMb: true,
      }),
    [chartState?.data]
  );

  const executeFetch = useCallback(() => {
    if (!deviceUuid) {
      setLoading(false);
      return;
    }

    let timestampISO;
    try {
      timestampISO = getUTCTimeFromRange(effectiveRangeKey);
    } catch (_) {
      setError(`Invalid history range`);
      setLoading(false);
      return;
    }

    const myGen = ++requestGen.current;
    setLoading(true);
    Promise.resolve()
      .then(() =>
        loadChartState({
          deviceUuid,
          timestampISO,
          interval: showInterval ? interval : null,
        })
      )
      .then((state) => {
        if (myGen !== requestGen.current) return;
        setChartState(state && state.data !== undefined ? state : { data: [], errorAreas: [] });
        setError(undefined);
      })
      .catch((err) => {
        if (myGen !== requestGen.current) return;
        const detail = err?.response?.data?.detail;
        const normalized = normalizeAxiosDetail(detail, err?.message || String(err));
        setChartState(undefined);
        setError(normalized);
      })
      .finally(() => {
        if (myGen !== requestGen.current) return;
        setLoading(false);
      });
  }, [
    deviceUuid,
    loadChartState,
    effectiveRangeKey,
    interval,
    showInterval,
  ]);

  const fetchRef = useRef(executeFetch);
  useEffect(() => {
    fetchRef.current = executeFetch;
  }, [executeFetch]);

  const debouncedRef = useRef();
  if (!debouncedRef.current) {
    debouncedRef.current = debounce(() => fetchRef.current(), debounceMs);
  }

  useEffect(() => {
    debouncedRef.current?.();
    return () => debouncedRef.current.cancel();
  }, [executeFetch]);

  if (
    !loading &&
    !error &&
    !chartState?.data?.length &&
    !chartState?.errorAreas?.length
  ) {
    return "No Data Available";
  }

  if (error && !loading && !chartState?.data?.length) {
    return (
      <Box sx={{ borderRadius: 2, minWidth: "80%", maxWidth: "80%" }}>
        <Alert severity="error">{renderAlertBody(error)}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: 2, minWidth: "80%", maxWidth: "80%" }}>
      {error && (
        <Box onClick={() => setError(undefined)}>
          <Alert severity="error">{renderAlertBody(error)}</Alert>
        </Box>
      )}
      <Grid container justifyContent="flex-end" spacing={10} mb={2}>
        {loading && <Loader />}
        {(showHistory || showInterval) && (
          <>
            {showHistory ? (
              <Grid item>
                <FormControl sx={{ width: 150 }}>
                  <InputLabel sx={{ color: "white" }} shrink>
                    History
                  </InputLabel>
                  <Select
                    fullWidth
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                    sx={{ color: "white", borderColor: "white" }}
                  >
                    {(historyOptions || MONITOR_HISTORY_RANGE_OPTIONS).map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            {showInterval ? (
              <Grid item>
                <FormControl sx={{ width: 150 }}>
                  <InputLabel shrink sx={{ color: "white" }}>
                    Interval
                  </InputLabel>
                  <Select
                    fullWidth
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                    sx={{ color: "white", borderColor: "white" }}
                  >
                    {intervalOptions.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
          </>
        )}
      </Grid>

      <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
        <LineChart
          data={chartState?.data}
          margin={{ top: 16, right: 28, bottom: 12, left: 12 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(128, 128, 128, 0.3)"
          />
          <XAxis
            dataKey="timestamp"
            stroke="#444"
            tickFormatter={(value) => new Date(value).toLocaleString()}
          />
          <YAxis
            yAxisId="left"
            orientation="left"
            domain={leftYDomain}
            stroke="#444"
            tickCount={8}
            tickFormatter={(value) => `${value} ms`}
            allowDecimals={false}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={rightYDomain}
            stroke="#444"
            tickCount={8}
            tickFormatter={(value) => `${value} MB`}
            allowDecimals={false}
          />
          {Object.entries(seriesDefinition).map(([key, cfg]) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={cfg.label}
              stroke={cfg.color}
              strokeWidth={2}
              dot={false}
              yAxisId={cfg.axis}
              isAnimationActive={false}
            />
          ))}
          <Legend />
          <Tooltip
            wrapperStyle={{ outline: "none" }}
            content={(tipProps) => (
              <MonitorTooltipBody
                {...tipProps}
                seriesDefinition={seriesDefinition}
                rows={chartState?.data}
              />
            )}
          />
          {(chartState?.errorAreas || []).map((area, index) => (
            <ReferenceArea
              key={index}
              x1={area.x1}
              x2={area.x2}
              fill="red"
              fillOpacity={0.2}
              yAxisId="left"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
